import {ContainerView} from "./Containers";
import {TransportType} from "../dto/com.rico.sb2.entity.device";
import {sortNumberAsc, sortNumberDesc} from "../lib/langExtensions";
import {PositionView} from "./Positions";
import {SchemaContainerTracksPlayerAdapter} from "./SchemaContainerTracks";
import {ContainerPathTrack, ContainerTrackGenerator, ContainerTrackLine} from "./ContainerTrackApi";
import {AppConfig} from "../AppConfig";

const TrackContainerDistance = 7;
const TrackWrapMarginX = 9;
const TrackGap = 4;


function getPositionUnderContainer(player: SchemaContainerTracksPlayerAdapter, containerView: ContainerView): number {
	if (containerView.onTransport) {
		const transportView = player.getTransportView(containerView.onTransport);
		if (transportView == null) throw new Error(`transfer #${containerView.onTransport} has no transport view instance`);
		if (transportView.position == null) throw new Error(`transfer #${containerView.onTransport} is not over a position`);
		return transportView.position;
	}
	if (containerView.onPosition) {
		return containerView.onPosition;
	}
	throw new Error(`cannot resolve container #${containerView.data.id} position - it's not on position or transport`);
}

function isOnAO(player: SchemaContainerTracksPlayerAdapter, containerView: ContainerView) {
	const transport = containerView.onTransport;
	return transport != null && player.getTransportView(transport).data.type == TransportType.AO;
}

function isOnTransferStart(player: SchemaContainerTracksPlayerAdapter, currentPosition: number, nextPosition: number) {
	return player.findTransferByMove(currentPosition, nextPosition) != null;
}

function trackLinesAppend(trackLines: ContainerTrackLine[], ...lines: ContainerTrackLine[]) {
	for (const l of lines) {
		const lastLine = trackLines.length == 0 ? null : trackLines[trackLines.length - 1];
		if (lastLine == null || !lastLine.append(l)) {
			trackLines.push(l);
		}
	}
}

export function createContainerTrackGenerator(player: SchemaContainerTracksPlayerAdapter) {
	return AppConfig.directPathsOnSchema
		? new DirectLineGenerator(player)
		: new SegmentLineGenerator(player)
}

class SegmentLineGenerator implements ContainerTrackGenerator {
	private readonly player: SchemaContainerTracksPlayerAdapter;

	constructor(player: SchemaContainerTracksPlayerAdapter) {
		this.player = player;
	}

	/**
     Переменная anchor - это наш старт. Теперь надо определиться, куда мы едем. В большинстве случаев (при переезде из ванны в ванну) надо просто трек
     подняться из ванны вверх и ехать вправо-влево. Но в некоторых случая - подниматься не надо, ведем линию прямо из контейнера. Это случаи такие:
     1) мы находимся на АО
     2) мы находимся на трансфере (и следующая точка - конец этого трансфера).

     Введем обозначение "якорная точка" для позиции - там, где трек останавливается и поворачивает вниз к позиции, чтобы закончить путь.

     Алгоритм такой:
     1) выбираем начальную точку (это центр контейнера)
     2) если надо подняться, то поднимаемся в якорную точку позиции контейнера.
     3) направляемся в якорную точку следующей позиции.
     4) повторяем шаг 3, пока не достигнем последней якорной точки
     5) опускаем трек к позиции.

     Теперь надо подумать как не пересечься с остальными треками.
     Сделаем так: каждый сегмент трека это тип линии (горизонтальная или вертикальная), основная координата линии (x - для вертикальной, y - для горизонтальной) и
     пара дополнительных координат для начала и конца ([y1, y2] для вертикальной, и [x1, x2] для горизонтальной). Координаты целые - integer.
     Далее - все отображаемые треки запоминаются (даже от других контейнеров). И дальше идем по сегментам и смотрим - пересекаются они с кем-то или нет. Если пересекаются -
     сдвигаем основную координату и меняем дополнительные координаты у сегмента до и после.

     Все это считается из расчета линии без переноса. Перенос делается вторым проходом, отдельно.
	 */
	makeTrackLines(track: ContainerPathTrack): ContainerTrackLine[] {
		const containerView = this.player.getContainerView(track.id);
		if (!containerView) return [];

		const containerLocation = containerView.onPosition ? `P${containerView.onPosition}` : `T${containerView.onTransport}`;
		console.info(`#${track.id} path: ${containerLocation} -> ${JSON.stringify(track.path)}`);


		const positions = this.player.getAllPositions();
		const positionsOnLineY = (lineY: number) => {
			return positions.filter(p => p.lineY == lineY).sort((a, b) => a.x - b.x);
		}
		const prevLineY = (lineY: number): number => {
			return this.player.getAllPositionLineY().filter(y => y < lineY).sort(sortNumberDesc)[0]
		}
		const nextLineY = (lineY: number): number => {
			return this.player.getAllPositionLineY().filter(y => y > lineY).sort(sortNumberAsc)[0]
		}

		function* createSameLineSegment(trackTail: { position: number, x: number, y: number }, nextPV: PositionView, prevPV: PositionView, mirrorSchema: boolean) {
			// Переноса нет, просто рисуем линию
			if (nextPV.lineY == prevPV.lineY) {
				yield ContainerTrackLine.horizontal(trackTail.y, trackTail.x, nextPV.centerX);
			}
			// А в этом случае делаем перенос, поскольку это однорядная линия с переносом.
			else {
				let start = prevPV, finish = nextPV;
				let startX = trackTail.x, startY = trackTail.y;
				let offsetY = startY - prevPV.y;

				// Переносов может быть много (в текущих условиях хватает одного, но все-таки)
				while (start.lineY != finish.lineY) {
					const startLinePositions = positionsOnLineY(start.lineY);

					// если для перехода на другую линию надо ехать влево ...
					let jumpAtLeft = start.lineY > finish.lineY;
					// применяем mirrorSchema - все в другую сторону
					if (mirrorSchema) {
						jumpAtLeft = !jumpAtLeft;
					}

					const nextLineYFunc = jumpAtLeft
						? (mirrorSchema ? nextLineY : prevLineY)
						: (mirrorSchema ? prevLineY : nextLineY);

					if (jumpAtLeft) {
						const segmentEnd = startLinePositions[0];
						const segmentEndX = Math.round(segmentEnd.x - TrackWrapMarginX);
						yield ContainerTrackLine.horizontal(startY, startX, segmentEndX, {markerEnd: 'svg-c-track-ew'})

						const nextLine = nextLineYFunc(start.lineY);
						if (!nextLine) break;
						start = positionsOnLineY(nextLine).reverse()[0];
						startX = start.x + start.width + TrackWrapMarginX;
					} else {
						const segmentEnd = startLinePositions[startLinePositions.length - 1];
						const segmentEndX = Math.round(segmentEnd.x + segmentEnd.width + TrackWrapMarginX);
						yield ContainerTrackLine.horizontal(startY, startX, segmentEndX, {markerEnd: 'svg-c-track-ew'})

						const nextLine = nextLineYFunc(start.lineY);
						if (!nextLine) break;
						start = positionsOnLineY(nextLine)[0];
						startX = start.x - TrackWrapMarginX;
					}

					startY = start.y + offsetY;
				}

				// И вот мы уже на нужной Y. Добавим последний проход
				yield ContainerTrackLine.horizontal(startY, startX, finish.centerX, {markerStart: 'svg-c-track-sw'})
			}
		}

		const trackLines: ContainerTrackLine[] = []
		const trackLineLast = () => trackLines[trackLines.length - 1];

		let trackTail = {position: getPositionUnderContainer(this.player, containerView), x: containerView.centerX, y: containerView.centerY};

		if (isOnAO(this.player, containerView) || isOnTransferStart(this.player, trackTail.position, track.path[0])) {
			// поскольку мы никуда не будет подниматься из этой точки - едем как ехали
		} else {
			const pv = this.player.getPositionView(trackTail.position);
			trackLines.push(ContainerTrackLine.vertical(containerView.centerX, containerView.centerY, pv.y - TrackContainerDistance));
			trackTail = {position: trackTail.position, ...trackLineLast().end}
		}

		let pathIndex = -1
		while (++pathIndex < track.path.length) {
			const prevPV = this.player.getPositionView(trackTail.position);
			const nextP = track.path[pathIndex];
			const nextPV = this.player.getPositionView(nextP);

			// Если мы на одной линии - линия будет горизонтальной. Ну или с разрывом, если там перенос линии включен
			if (prevPV.data.line == nextPV.data.line) {
				trackLinesAppend(trackLines, ...Array.from(createSameLineSegment(trackTail, nextPV, prevPV, this.player.mirrorSchema)));
				trackTail = {position: nextP, ...trackLineLast().end}
			}
			// Иначе это трансфер. И тут линия будет вертикальной
			else {
				trackLinesAppend(trackLines, ContainerTrackLine.vertical(trackTail.x, trackTail.y, nextPV.y - TrackContainerDistance));
				trackTail = {position: nextP, ...trackLineLast().end}
			}
		}

		// и добавляем последний опуск
		const lastPV = this.player.getPositionView(trackTail.position);
		trackLines.push(ContainerTrackLine.vertical(trackLineLast().end.x, trackLineLast().end.y, lastPV.y))

		return trackLines;
	}

	/**
	 * Теперь смотрим где что пересекается и убираем пересечения.
	 * Надо вытащить все линии (не текущего трека) и посмотреть с чем есть пересечения.
	 *
	 * Бежим, ищем совпадения. Сначала вертикальные, потом горизонтальные.
	 * Вертикальные линии можно смещать и влево и вправо. Горизонтальные только вверх.
	 * @private
	 */
	avoidIntersections(containerTracks: Map<number, ContainerPathTrack>, id: number, trackLines: ContainerTrackLine[]) {
		const otherLines = Array.from(containerTracks.values()).filter(t => t.id != id).flatMap(t => t.trackLines)
		const otherVerticals = otherLines.filter(l => l.vertical);
		const otherHorizontals = otherLines.filter(l => !l.vertical);
		if (otherHorizontals.length == 0 && otherVerticals.length == 0) {
			return;
		}

		function sameSpanNoOverlap(line: ContainerTrackLine, sameSpan: ContainerTrackLine[]) {
			if (sameSpan.length == 0) {
				return true;
			}
			// Так, есть пересечение по второстепенной координате. Если нет по основной - то работаем как есть.
			return !sameSpan.some(ev => ev.main == line.main);
		}

		function alignEnds(line: ContainerTrackLine, index: number, array: ContainerTrackLine[]) {
			if (index >= 1 && !line.markerStart && !array[index - 1].markerEnd) {
				array[index - 1].sec2 = line.main;
			}
			if (index < array.length - 1 && !line.markerEnd && !array[index + 1].markerStart) {
				array[index + 1].sec1 = line.main;
			}
		}

		trackLines.forEach((line, index, array) => {
			if (!line.vertical) return;

			// Проверяем пересечение по второстепенной координате
			const sameSpan = otherVerticals.filter(ev => ev.isSameSpan(line));
			if (sameSpanNoOverlap(line, sameSpan)) {
				return;
			}
			// И тут пересечение. Тогда пробуем наш main менять вправо-влево, пока не попадем в пустое место.
			const lineMain = line.main;
			let step = 0, newMain = lineMain;
			do {
				step += 1;
				newMain = lineMain + TrackGap * (step % 2 == 0 ? (step / 2) : ((step + 1) / -2))
			} while (sameSpan.some(ev => ev.main == newMain));

			// Нашли. Есть новая координата. Теперь надо сместить конец предыдущего сегмента и начало следующего, если наша линия не обрывается.
			line.main = newMain;
			alignEnds(line, index, array);
		});

		trackLines.forEach((line, index, array) => {
			if (line.vertical) return;

			// Проверяем пересечение по второстепенной координате
			const sameSpan = otherHorizontals.filter(ev => ev.isSameSpan(line));
			if (sameSpanNoOverlap(line, sameSpan)) {
				return;
			}

			// И тут пересечение. Тогда пробуем наш main менять вверх, пока не попадем в пустое место.
			const lineMain = line.main;
			let step = 0, newMain = lineMain;
			do {
				step += 1;
				newMain = lineMain - TrackGap * step;
			} while (sameSpan.some(ev => ev.main == newMain));

			// Нашли. Есть новая координата. Теперь надо сместить конец предыдущего сегмента и начало следующего, если наша линия не обрывается.
			line.main = newMain;
			alignEnds(line, index, array);
		});
	}
}

class DirectLineGenerator implements ContainerTrackGenerator {
	private readonly player: SchemaContainerTracksPlayerAdapter;

	constructor(player: SchemaContainerTracksPlayerAdapter) {
		this.player = player;
	}

	makeTrackLines(track: ContainerPathTrack): ContainerTrackLine[] {
		const containerView = this.player.getContainerView(track.id);
		if (!containerView) return [];

		const trackLines: ContainerTrackLine[] = []
		const trackLineLast = () => trackLines[trackLines.length - 1];

		let trackTail = {position: getPositionUnderContainer(this.player, containerView), x: containerView.centerX, y: containerView.centerY};

		if (isOnAO(this.player, containerView) || isOnTransferStart(this.player, trackTail.position, track.path[0])) {
			// поскольку мы никуда не будет подниматься из этой точки - едем как ехали
		} else {
			const pv = this.player.getPositionView(trackTail.position);
			trackLines.push(ContainerTrackLine.vertical(containerView.centerX, containerView.centerY, pv.y - TrackContainerDistance));
			trackTail = {position: trackTail.position, ...trackLineLast().end}
		}

		let pathIndex = -1
		while (++pathIndex < track.path.length) {
			const prevPV = this.player.getPositionView(trackTail.position);
			const nextP = track.path[pathIndex];
			const nextPV = this.player.getPositionView(nextP);

			trackLinesAppend(trackLines, ContainerTrackLine.direct(trackTail.x, trackTail.y, nextPV.centerX, nextPV.y - TrackContainerDistance));
			trackTail = {position: nextP, ...trackLineLast().end}
		}

		// и добавляем последний опуск
		const lastPV = this.player.getPositionView(trackTail.position);
		trackLines.push(ContainerTrackLine.vertical(trackLineLast().end.x, trackLineLast().end.y, lastPV.y))

		return trackLines;
	}

	avoidIntersections(containerTracks: Map<number, ContainerPathTrack>, id: number, trackLines: ContainerTrackLine[]): void {
		// а ничего не надо делать, пусть болтается
	}
}

